import { FormattedMessage, FormattedReactMessage } from '@99designs/frontend-components/i18n/';
import { CheckList } from '../../../../shared/components/List';
import Paragraph from '../../../../shared/components/Paragraph';
import Link from '../../../../shared/components/Link';
import Box from '../../../../shared/spa/core-components/Box';
import { getLogoPrice, stringContainsPrice } from '../LogoDesignPricing/helpers';
import type { Currency as CurrencyCode } from '../../../../shared/lib/currency';
import type { ProductDetail } from './DetailsSection';
import type { DesignerImage } from './Showcase';
import { CurrencyRange } from '../../I18n/CurrencyRange';

export const getLinkReplacements = (productDetail: ProductDetail) => {
    const replacements: { [index: string]: JSX.Element } = {};

    productDetail.leadingParagraph?.links?.forEach((link) => {
        replacements[link.anchor] = <Link href={link.address} color="white" />;
    });

    return replacements;
};

export const getFeatureReplacements = (
    productDetail: ProductDetail,
    currency: CurrencyCode
): React.ReactNode[] => {
    return productDetail.features.map((feature) => {
        if (!stringContainsPrice(feature)) {
            return feature;
        }
        return (
            <CurrencyRange
                string={feature}
                fromPriceValue={getLogoPrice('base-option', currency)}
                toPriceValue={getLogoPrice('platinum-option', currency)}
                currency={currency}
                hideDecimal
            />
        );
    });
};

export const getProductDetails = (productDetails: ProductDetail[], currency: CurrencyCode) =>
    productDetails.map((productDetail) => {
        const replacements =
            productDetail.leadingParagraph && productDetail.leadingParagraph.links
                ? getLinkReplacements(productDetail)
                : {};

        const features = productDetail.features.find((feature) => stringContainsPrice(feature))
            ? getFeatureReplacements(productDetail, currency)
            : productDetail.features;

        return {
            id: productDetail.id,
            title: productDetail.title,
            child: (
                <>
                    {productDetail.leadingParagraph && productDetail.leadingParagraph.copy && (
                        <Box paddingBottom="space10">
                            <Paragraph variations={['no-margin']} paddingbottom="space10">
                                <FormattedReactMessage
                                    message={productDetail.leadingParagraph.copy}
                                    replacements={replacements}
                                />
                            </Paragraph>
                        </Box>
                    )}

                    <CheckList withDivider={true} items={features} />

                    {productDetail.endingParagraph && (
                        <Box paddingTop="space10">
                            <Paragraph variations={['no-margin']}>
                                {productDetail.endingParagraph}
                            </Paragraph>
                        </Box>
                    )}
                </>
            ),
        };
    });

export const getShowcaseDesigner = (
    randomFeaturedDesignerNumber: number,
    designerImages: DesignerImage[]
): DesignerImage => {
    // There's a chance that the random index returns the length of the designer images
    // which is why we need Math.min here.
    // Using Math.floor here over Math.round to ensure the changes of each designer is fair
    const featuredDesignerIndex = Math.min(
        Math.floor(randomFeaturedDesignerNumber * designerImages.length),
        designerImages.length - 1
    );

    return designerImages[featuredDesignerIndex];
};
